import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Empresa } from '../models/empresa.model';
import { PersonaFiltro } from '../models/persona-filtro.model';
import { VendedorRequest } from '../models/vendedor.model';
import { LoadingService } from '../services/loading.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient, private loadingSrv: LoadingService) {}

  public urlWeb: string = environment.apiUrl;

  /* A tener en cuenta:
  A todos los API que se llama se le debe pasar el Headers con el Token obtenido en la sesion */

  /*   public urlWeb: string = 'http://localhost:8089/RiberaSalud/api'; */
  private readonly TOKEN_KEY = 'token';
  private readonly USUARIO_KEY = 'usuario';
  private readonly ROLES_KEY = 'roles';
  mesesInicialesDJ: number = 3;

  guardarToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  obtenerToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  crearEncabezadoAutorizacion(): HttpHeaders {
    const token = this.obtenerToken();
    return token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();
  }

  guardarUsuario(usuario: any): void {
    localStorage.setItem(this.USUARIO_KEY, JSON.stringify(usuario));
    if (usuario.roles) {
      localStorage.setItem(this.ROLES_KEY, JSON.stringify(usuario.roles));
    }
  }

  obtenerUsuario(): any | null {
    const usuario = localStorage.getItem(this.USUARIO_KEY);
    return usuario ? JSON.parse(usuario) : null;
  }

  obtenerRoles(): string[] {
    const roles = localStorage.getItem(this.ROLES_KEY);
    return roles ? JSON.parse(roles) : [];
  }

  obtenerNombreUsuario(): string | null {
    const usuario = this.obtenerUsuario();
    return usuario.nombreCompleto;
  }

  obtenerUsuarioPorNombreUsuario(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/usuarios/usuario`, {
        headers,
      })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerPlanes(): Observable<any[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/planes/todos`, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerPlanesActivos(): Observable<any[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/planes/planesActivos`, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerPersonas(estado: 'activos' | 'todos' = 'todos'): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    const url =
      estado === 'activos'
        ? `${this.urlWeb}/personas/activos`
        : `${this.urlWeb}/personas/todos`;
    return this.http.get<any[]>(url, { headers }).pipe(
      tap(() => this.loadingSrv.setLoading(false)),
      catchError((error) => {
        this.loadingSrv.setLoading(false);
        return throwError(error);
      })
    );
  }

  obtenerIncongruencias() {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/personasduplicadas`, { headers })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerTiposBeneficiarios(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/tipobeneficiario/obtenerTodos`, { headers })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerTiposBeneficiariosExcluyendoRelacionDependencia(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(
        `${this.urlWeb}/tipobeneficiario/obtenerTodosLosTipoBeneficioExcluyendoRelacionDependencia`,
        { headers }
      )
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  exportarArchivosCompletos(): Observable<Blob> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get(`${this.urlWeb}/personas/altasGestosTodos`, {
        headers,
        responseType: 'blob',
      })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  exportarArchivos(
    tipoArchivo: string,
    fechaDesde: string,
    fechaHasta: string
  ): Observable<Blob> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();

    // Determina el endpoint según el tipo de archivo
    let endpoint = '';
    switch (tipoArchivo.toLowerCase()) {
      case 'altas':
        endpoint = `${this.urlWeb}/personas/altasGestos?mes=${fechaDesde}&anio=${fechaHasta}`;
        break;
      default:
        this.loadingSrv.setLoading(false);
        return throwError(() => new Error('Tipo de archivo no válido'));
    }

    return this.http
      .get(endpoint, {
        headers,
        responseType: 'blob',
        params: { fechaDesde: fechaDesde, fechaHasta: fechaHasta },
      })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerReporteTitularesSinDj(): Observable<Blob> {
    this.loadingSrv.setLoading(true);
    const encodedMeses = btoa(this.mesesInicialesDJ.toString());
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get(`${this.urlWeb}/personas/titularesSinDj?meses=${encodedMeses}`, {
        headers,
        responseType: 'blob',
      })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerTitularesYAdherentes(filtro: PersonaFiltro): Observable<any[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post<any[]>(`${this.urlWeb}/personas/personasfiltradas`, filtro, {
        headers,
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerUsuarios() {
    const headers = this.crearEncabezadoAutorizacion();
    const endpoint = `${this.urlWeb}/usuarios/todos`;
    return this.http.get<any[]>(endpoint, { headers }).pipe(
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  obtenerPlanFijoPorCuil(cuil: string): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    const encodedCuil = btoa(cuil);
    return this.http
      .get(`${this.urlWeb}/planes/planfijo/${encodedCuil}`, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerObrasSociales(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/obrasocial/obtenerTodosLasObrasSociales`, {
        headers,
      })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerSexos(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/sexo/todos`, { headers })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerEstadosCiviles(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/estadoCivil/todos`, { headers })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerNacionalidades(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/nacionalidad/todos`, { headers })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerParentesco(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/parentesco/todos`, { headers })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerRevistas(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/revista/todos`, { headers })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerProvincias(): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/provincias`, { headers })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerLocalidadesSegunIdProvincia(idProvincia: number): Observable<any[]> {
    this.loadingSrv.setLoading(true);
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/localidades`, {
        headers,
        params: { idProvincia },
      })
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  obtenerSalarioMVM(): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get(`${this.urlWeb}/planes/salarioMVMReciente`, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  guardarSalioMVM(salarioMVMString: string): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    const usuario = this.obtenerNombreUsuario();
    return this.http
      .post(
        `${this.urlWeb}/planes/guardarSalarioMVM`,
        { salarioMVMString, usuario },
        { headers }
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  guardarPlan(nuevoPlan: any): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(`${this.urlWeb}/planes/guardarPlan`, nuevoPlan, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  actualizarPlan(nuevoPlan: any): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(`${this.urlWeb}/planes/actualizarPlan`, nuevoPlan, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  agregarPersona(nuevaPersona: any): Observable<HttpResponse<any>> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(`${this.urlWeb}/personas/guardarPersona`, nuevaPersona, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  actualizarPersona(persona: any): Observable<HttpResponse<any>> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(`${this.urlWeb}/personas/actualizarPersona`, persona, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error: any) => {
          return throwError(() => error);
        })
      );
  }

  subirArchivos(
    archivos: { archivo: File; tipo: string }[],
    obraSocialSeleccionada: string,
    periodoPercibido: number = 0
  ): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    const formData = new FormData();
    formData.append('codigoObraSocialStr', obraSocialSeleccionada);
    formData.append('periodoPercibido', periodoPercibido.toString());

    archivos.forEach((item, index) => {
      formData.append('tiposArchivos', item.tipo);
      formData.append('archivos', item.archivo);
    });

    return this.http
      .post(`${this.urlWeb}/importar/procesarArchivo`, formData, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerDescripcionesEnumPorCodigo(codigo: number): Observable<string[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<string[]>(`${this.urlWeb}/importar/tipo-archivos?codigo=${codigo}`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerVersionApiYDb(): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http.get(`${this.urlWeb}/version`, { headers }).pipe(
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  obtenerPersonasIncongruentes(): Observable<any[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/personaIncongruente/todas`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerPersona(cuil: string): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any>(`${this.urlWeb}/personas/persona/${cuil}`, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  resolverPersonaIncongruente(personaDTO: any): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(`${this.urlWeb}/personas/resolverPersonaIncongruente`, personaDTO, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error: any) => {
          return throwError(() => error);
        })
      );
  }

  eliminarPlan(idPlan: number): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .delete(`${this.urlWeb}/planes/eliminarplan`, {
        headers,
        params: { idPlan: idPlan.toString() },
        responseType: 'json',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerTodosLosRoles(): Observable<any[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/usuarios/obtenerRoles`, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  crearUsuario(usuario: any): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(`${this.urlWeb}/usuarios/crear`, usuario, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  actualizarUsuario(id: string, usuario: any): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .put(`${this.urlWeb}/usuarios/actualizar/${id}`, usuario, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  eliminarUsuario(idUsuario: number): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    const encodedIdUsuario = btoa(idUsuario.toString());
    return this.http
      .delete(`${this.urlWeb}/usuarios/delete/${encodedIdUsuario}`, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  verificarPersonaExistente(cuil: string): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get(`${this.urlWeb}/personas/personaExistente/${cuil}`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  actualizarCodigosEstadosCiviles(estadosCiviles: any[]): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(
        `${this.urlWeb}/personas/actualizarCodigoGestosEstadoCivil`,
        estadosCiviles,
        {
          headers,
          observe: 'response',
          responseType: 'text',
        }
      )
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  actualizarCodigosParentescos(parentescos: any[]): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(
        `${this.urlWeb}/personas/actualizarCodigoGestosParentesco`,
        parentescos,
        {
          headers,
          observe: 'response',
          responseType: 'text',
        }
      )
      .pipe(
        tap(() => this.loadingSrv.setLoading(false)),
        catchError((error) => {
          this.loadingSrv.setLoading(false);
          return throwError(error);
        })
      );
  }

  buscarPersonas(termino: string): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any>(`${this.urlWeb}/personas/buscarPersonas/${termino}`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerAdherentesPorTitular(cuilTitular: string): Observable<any[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/buscarAdherentes/${cuilTitular}`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerVendedores(): Observable<any[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/vendedores`, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerVendedoresActivos(): Observable<any[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/personas/vendedores/activos`, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  crearVendedor(vendedor: VendedorRequest): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(`${this.urlWeb}/personas/vendedores/crear`, vendedor, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  actualizarVendedor(vendedor: VendedorRequest): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .put(`${this.urlWeb}/personas/vendedores/editar`, vendedor, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  obtenerEmpresas(): Observable<any[]> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<any[]>(`${this.urlWeb}/empresas/todas`, { headers })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  crearEmpresa(empresa: Empresa): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(`${this.urlWeb}/empresas/crear`, empresa, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  actualizarEmpresa(empresa: Empresa): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .put(`${this.urlWeb}/empresas/editar`, empresa, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  eliminarEmpresa(cuit: number): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .delete(`${this.urlWeb}/empresas/eliminar/${cuit}`, {
        headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  reasignarPlanes(): Observable<any> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .post(
        `${this.urlWeb}/planes/reasignarPlanes`,
        {},
        {
          headers,
          responseType: 'text',
        }
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  consultarEstadoPago(): Observable<{ esDeudor: boolean }> {
    const headers = this.crearEncabezadoAutorizacion();
    return this.http
      .get<{ esDeudor: boolean }>(
        `${this.urlWeb}/estadocuenta/obtenerEstadoCuenta`,
        {
          headers,
        }
      )
      .pipe(
        catchError((error) => {
          return throwError({ esDeudor: false });
        })
      );
  }
}
